import React from 'react';
import { Mail, MapPin, Phone, Instagram, Facebook, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

export function Footer() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <footer className={`border-t pt-20 pb-10 transition-colors duration-500 ${isDark ? 'bg-[#111827] border-gray-700' : 'bg-white border-gray-100'}`} role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3.5 mb-6">
              <img 
                src="/logo.png" 
                alt="Harmony Home Designs Logo" 
                className="w-11 h-11 object-contain transition-all hover:scale-105 hover:drop-shadow-[0_0_8px_rgba(201,162,107,0.4)] duration-300"
              />
              <div className="flex flex-col justify-center">
                <a href="#" className={`font-poppins font-bold text-[1.4rem] tracking-tight leading-none ${isDark ? 'text-white' : 'text-primary'}`}>
                  AURA<span className="text-accent">SPACES</span>
                </a>
                <span className={`text-[0.55rem] font-semibold tracking-[0.15em] uppercase mt-1 transition-colors ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  by Harmony Home Designs
                </span>
              </div>
            </div>
            <p className={`mb-6 text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              We design and build premium interiors that define your lifestyle. End-to-end solutions, transparent pricing, and unmatched quality.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.instagram.com/auraspaces_hhd?igsh=bjlmc2h5Zmt0MjV0" target="_blank" aria-label="Aura Spaces on Instagram" rel="noopener noreferrer" className={`w-10 h-10 rounded-full flex items-center justify-center hover:bg-accent hover:text-white transition-colors ${isDark ? 'bg-white/10 text-gray-300' : 'bg-slate-50 text-primary'}`}>
                <Instagram size={18} />
              </a>
              <a href="https://www.facebook.com/people/Aura-Spaces/61557697775548/" target="_blank" aria-label="Aura Spaces on Facebook" rel="noopener noreferrer" className={`w-10 h-10 rounded-full flex items-center justify-center hover:bg-accent hover:text-white transition-colors ${isDark ? 'bg-white/10 text-gray-300' : 'bg-slate-50 text-primary'}`}>
                <Facebook size={18} />
              </a>
              <a href="https://www.linkedin.com/company/aura-spaces-hhd/" target="_blank" aria-label="Aura Spaces on LinkedIn" rel="noopener noreferrer" className={`w-10 h-10 rounded-full flex items-center justify-center hover:bg-accent hover:text-white transition-colors ${isDark ? 'bg-white/10 text-gray-300' : 'bg-slate-50 text-primary'}`}>
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <div>
            <h4 className={`font-poppins font-bold mb-6 ${isDark ? 'text-white' : 'text-primary'}`}>Quick Links</h4>
            <ul className="space-y-3 text-sm mb-8">
              <li><a href="#" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Home</a></li>
              <li><a href="#services" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Our Services</a></li>
              <li><a href="#projects" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Portfolio</a></li>
              <li><a href="#process" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>How We Work</a></li>
            </ul>
            <h4 className={`font-poppins font-bold mb-4 ${isDark ? 'text-white' : 'text-primary'}`}>Our Branches</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/interior-designers-in-hyderabad" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hyderabad</Link></li>
              <li><Link to="/interior-designers-in-vijayawada" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Vijayawada</Link></li>
              <li><Link to="/interior-designers-in-bangalore" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Bangalore</Link></li>
            </ul>
          </div>

          <div>
            <h4 className={`font-poppins font-bold mb-6 ${isDark ? 'text-white' : 'text-primary'}`}>Services</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Residential Interiors</a></li>
              <li><a href="#" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Commercial Setup</a></li>
              <li><a href="#" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Modular Kitchens</a></li>
              <li><a href="#" className={`hover:text-accent transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Turnkey Construction</a></li>
            </ul>
          </div>

          <div>
            <h4 className={`font-poppins font-bold mb-6 ${isDark ? 'text-white' : 'text-primary'}`}>Contact Us</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start">
                <Phone className="w-5 h-5 text-accent mr-3 shrink-0" />
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>+91 9989697942</span>
              </li>
              <li className="flex items-start">
                <Mail className="w-5 h-5 text-accent mr-3 shrink-0" />
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>operations@auraspaces.net</span>
              </li>
              <li className="flex items-start">
                <MapPin className="w-5 h-5 text-accent mr-3 shrink-0 mt-0.5" />
                <div className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                  <div className="mb-3">
                    <strong className={`block font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Design Studios</strong>
                    Hyderabad & Vijayawada
                  </div>
                  <div>
                    <strong className={`block font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Serving Regions</strong>
                    Telangana, Andhra Pradesh, Karnataka, Tamil Nadu
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className={`border-t pt-8 flex flex-col md:flex-row justify-between items-center text-xs ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
          <p>&copy; {new Date().getFullYear()} Aura Spaces. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy-policy" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-accent'}`}>Privacy Policy</Link>
            <Link to="/terms-of-service" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-accent'}`}>Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
